/*
 * ParLinx cart — the single owner of cart mutations. No dependencies.
 *
 * Talks to the Cart AJAX API (/cart/add.js, /cart/change.js) and re-renders
 * the drawer by requesting this section back via the `sections` param, then
 * swapping the [data-plx-drawer-content] node. Mutations are serialized
 * through a promise queue so rapid stepper taps can't race.
 *
 * Events (on document):
 *   plx:cart:add     {id, quantity}  — request to add a variant (PDP fires this)
 *   plx:cart:updated {itemCount}     — after any successful mutation
 *   plx:cart:error   {message}       — after a failed mutation
 */
(function () {
  var drawer = null;
  var sectionId = null;
  var queue = Promise.resolve();
  var lastTrigger = null;

  function statusNode() {
    return drawer ? drawer.querySelector("[data-plx-cart-status]") : null;
  }
  function contentNode() {
    return drawer ? drawer.querySelector("[data-plx-drawer-content]") : null;
  }

  function lenisStop() {
    if (window.__plxLenis) window.__plxLenis.stop();
  }
  function lenisStart() {
    if (window.__plxLenis) window.__plxLenis.start();
  }

  function announce(message) {
    var node = statusNode();
    if (node) {
      node.textContent = "";
      // Force a fresh announcement even for repeated messages.
      window.requestAnimationFrame(function () {
        node.textContent = message;
      });
    }
  }

  function openDrawer() {
    if (!drawer || drawer.open) return;
    drawer.showModal();
    lenisStop();
  }

  function closeDrawer() {
    if (drawer && drawer.open) drawer.close();
  }

  function updateBadges(count) {
    var badges = document.querySelectorAll("[data-plx-cart-count]");
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count > 0 ? String(count) : "";
      badges[i].classList.add("is-bumped");
      (function (badge) {
        window.setTimeout(function () {
          badge.classList.remove("is-bumped");
        }, 400);
      })(badges[i]);
    }
  }

  function swapDrawerContent(sectionHtml) {
    var current = contentNode();
    if (!current || !sectionHtml) return null;
    var doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    var fresh = doc.querySelector("[data-plx-drawer-content]");
    if (!fresh) return null;
    current.replaceWith(fresh);
    return parseInt(fresh.getAttribute("data-cart-count") || "0", 10);
  }

  function fetchDrawerSection() {
    // Fallback path when a mutation response carried no rendered section.
    return window
      .fetch("/?sections=" + encodeURIComponent(sectionId), { headers: { Accept: "application/json" } })
      .then(function (res) { return res.json(); })
      .then(function (sections) { return sections[sectionId]; });
  }

  function afterMutation(sections) {
    var html = sections && sections[sectionId];
    var apply = html ? Promise.resolve(html) : fetchDrawerSection();
    return apply.then(function (sectionHtml) {
      var count = swapDrawerContent(sectionHtml);
      if (count !== null) {
        updateBadges(count);
        document.dispatchEvent(
          new CustomEvent("plx:cart:updated", { detail: { itemCount: count } })
        );
        return count;
      }
      return null;
    });
  }

  function mutationFailed(payload) {
    var drawer = document.querySelector("[data-plx-cart-drawer]");
    var fallback =
      (drawer && drawer.getAttribute("data-error-text")) ||
      "Something went wrong. Please try again.";
    var message =
      (payload && (payload.description || payload.message)) || fallback;
    announce(message);
    document.dispatchEvent(new CustomEvent("plx:cart:error", { detail: { message: message } }));
  }

  function enqueue(job) {
    queue = queue.then(job, job);
    return queue;
  }

  function addToCart(id, quantity) {
    return enqueue(function () {
      return window
        .fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: id, quantity: quantity || 1 }],
            sections: sectionId,
            sections_url: window.location.pathname,
          }),
        })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) {
              mutationFailed(body);
              return null;
            }
            return afterMutation(body.sections).then(function (count) {
              announce("Added to bag.");
              openDrawer();
              return count;
            });
          });
        })
        .catch(function () {
          mutationFailed(null);
        });
    });
  }

  function changeLine(lineKey, quantity, lineEl, reloadAfter) {
    if (lineEl) lineEl.classList.add("is-busy");
    return enqueue(function () {
      return window
        .fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity,
            sections: sectionId,
            sections_url: window.location.pathname,
          }),
        })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) {
              if (lineEl) lineEl.classList.remove("is-busy");
              mutationFailed(body);
              return null;
            }
            if (reloadAfter) {
              window.location.reload();
              return null;
            }
            return afterMutation(body.sections);
          });
        })
        .catch(function () {
          if (lineEl) lineEl.classList.remove("is-busy");
          mutationFailed(null);
        });
    });
  }

  function onDocumentClick(e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest("[data-plx-cart-open]");
    if (opener && drawer) {
      e.preventDefault();
      lastTrigger = opener;
      openDrawer();
      return;
    }

    var closer = target.closest("[data-plx-cart-close]");
    if (closer && drawer && drawer.open) {
      closeDrawer();
      return;
    }

    // Line controls work both inside the drawer (section re-render) and on
    // the /cart page (full reload after the mutation).
    var qtyBtn = target.closest("[data-plx-qty]");
    if (qtyBtn) {
      var line = qtyBtn.closest("[data-line-key]");
      if (!line) return;
      var inDrawer = !!(drawer && drawer.contains(qtyBtn));
      var valueEl = line.querySelector(".plx-cart-line__qty-value");
      var current = parseInt((valueEl && valueEl.textContent) || "1", 10);
      var next = Math.max(0, current + parseInt(qtyBtn.getAttribute("data-plx-qty"), 10));
      changeLine(line.getAttribute("data-line-key"), next, line, !inDrawer);
      return;
    }

    var removeBtn = target.closest("[data-plx-remove]");
    if (removeBtn) {
      var removeLine = removeBtn.closest("[data-line-key]");
      if (!removeLine) return;
      var removeInDrawer = !!(drawer && drawer.contains(removeBtn));
      changeLine(removeLine.getAttribute("data-line-key"), 0, removeLine, !removeInDrawer);
    }
  }

  var documentListenersBound = false;

  function init() {
    drawer = document.querySelector("[data-plx-cart-drawer]");
    if (!drawer || drawer.dataset.plxInited) return;
    drawer.dataset.plxInited = "1";
    sectionId = drawer.getAttribute("data-section-id");

    if (!documentListenersBound) {
      documentListenersBound = true;
      document.addEventListener("click", onDocumentClick);
      document.addEventListener("plx:cart:add", function (e) {
        if (e.detail && e.detail.id) addToCart(e.detail.id, e.detail.quantity);
      });
    }

    drawer.addEventListener("close", function () {
      lenisStart();
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    });

    // Backdrop click closes (native dialog: a click whose target is the
    // dialog element itself is outside the panel content).
    drawer.addEventListener("click", function (e) {
      if (e.target === drawer) closeDrawer();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // Shopify theme editor: re-init when the section reloads
  document.addEventListener("shopify:section:load", init);
})();
